"use client";

import { useEffect, useRef, useState } from "react";
import { CookieConsent } from "../lib/consent";
import { useLanguage } from "../context/LanguageContext";

type CookiePreferencesModalProps = {
  open: boolean;
  initialPreferences: CookieConsent;
  onClose: () => void;
  onSave: (preferences: CookieConsent) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
};

type PreferenceKey = "analytics" | "personalization" | "marketing";

export function CookiePreferencesModal({
  open,
  initialPreferences,
  onClose,
  onSave,
  onAcceptAll,
  onRejectAll
}: CookiePreferencesModalProps): JSX.Element | null {
  const [draft, setDraft] = useState<CookieConsent>(initialPreferences);
  const dialogRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();
  const copy = t.cookie.modal;

  useEffect(() => {
    if (open) {
      setDraft(initialPreferences);
    }
  }, [initialPreferences, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    const previousFocus = document.activeElement as HTMLElement | null;
    const focusable = Array.from(
      dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || focusable.length === 0) {
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    first?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus();
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const updateDraft = (key: PreferenceKey, value: boolean): void => {
    setDraft((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const PreferenceToggle = ({
    label,
    description,
    value,
    disabled,
    onChange
  }: {
    label: string;
    description: string;
    value: boolean;
    disabled?: boolean;
    onChange?: (value: boolean) => void;
  }): JSX.Element => {
    const active = value;
    const baseClasses =
      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600";
    const activeClasses = active
      ? "bg-primary-600"
      : "bg-slate-300 dark:bg-slate-600";

    return (
      <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
        <div className="space-y-1">
          <p className="font-semibold text-slate-900 dark:text-slate-100">{label}</p>
          <p className="text-sm text-slate-600 dark:text-slate-300">{description}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={active}
          disabled={disabled}
          onClick={() => onChange?.(!active)}
          className={`${baseClasses} ${activeClasses} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              active ? "ltr:translate-x-5 rtl:-translate-x-5" : "ltr:translate-x-1 rtl:-translate-x-1"
            }`}
          />
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 sm:px-6">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/60 backdrop-blur"
        aria-label={copy.closeLabel ?? copy.title}
        onClick={onClose}
      />
      <div
        className="relative z-10 w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-900"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-preferences-title"
        aria-describedby="cookie-preferences-description"
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2
              id="cookie-preferences-title"
              className="text-xl font-bold text-slate-900 dark:text-slate-100"
            >
              {copy.title}
            </h2>
            <p
              id="cookie-preferences-description"
              className="mt-1 text-sm text-slate-600 dark:text-slate-300"
            >
              {copy.intro}
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <PreferenceToggle
            label={copy.essentialTitle}
            description={copy.essentialDescription}
            value={true}
            disabled
          />
          <PreferenceToggle
            label={copy.analyticsTitle}
            description={copy.analyticsDescription}
            value={draft.analytics}
            onChange={(value) => updateDraft("analytics", value)}
          />
          <PreferenceToggle
            label={copy.personalizationTitle}
            description={copy.personalizationDescription}
            value={draft.personalization}
            onChange={(value) => updateDraft("personalization", value)}
          />
          <PreferenceToggle
            label={copy.marketingTitle}
            description={copy.marketingDescription}
            value={draft.marketing}
            onChange={(value) => updateDraft("marketing", value)}
          />
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onRejectAll}
            className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 sm:w-auto"
          >
            {copy.rejectAll}
          </button>
          <button
            type="button"
            onClick={onAcceptAll}
            className="w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-primary-500 dark:hover:bg-primary-400 sm:w-auto"
          >
            {copy.acceptAll}
          </button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 sm:w-auto"
          >
            {copy.save}
          </button>
        </div>
      </div>
    </div>
  );
}
